import React from 'react';
import useTheme from '@cvent/blocks/hooks/useTheme';
import useBreakpoints from '@hooks/useBreakpoints';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { useAttendeeMessaging } from '@context/AttendeeMessagingContext';
import { useTranslate } from 'nucleus-text';
import { CSSObject } from '@emotion/react';
import Icon from '@components/shared/Icon';
import MessageBadge from './MessageBadge';

const useStyle = (): Record<string, CSSObject> => {
  const { isMobile } = useBreakpoints();
  const { font, size, block, backgroundColor } = useTheme();
  const { unreadCount } = useAttendeeMessaging();

  return {
    wrapper: {
      display: 'flex',
      alignItems: 'center'
    },
    icon: {
      marginRight: !isMobile ? 0 : 12,
      paddingTop: !isMobile ? 1 : 0,
      marginTop: isMobile ? 4 : 0,
      position: 'relative'
    },
    badge: {
      position: 'absolute',
      left: '16px',
      top: '-6px',
      border: unreadCount ? `2px solid ${block.base}` : 'none',
      borderRadius: size.border.radius.round
    },
    activeBadge: {
      borderColor: backgroundColor.brand.hover
    },
    contentWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      flexGrow: '1',
      flexBasis: 'auto'
    },
    content: {
      fontSize: font.base.size.m,
      color: font.color.base
    }
  };
};

function MessageIcon({ iconOnly, isFlyoutVisible }: { iconOnly?: boolean; isFlyoutVisible?: boolean }): JSX.Element {
  const { font } = useTheme();
  const { translate } = useTranslate();
  const { wrapper, content, icon, badge, contentWrapper, activeBadge } = useStyle();
  return (
    <div css={wrapper} {...injectTestId('attendee-messaging-message-icon')}>
      <div css={icon}>
        <Icon name="message-bubble" size="md" color={font.color.base} aria-hidden />
        {iconOnly && (
          <div
            css={[badge, isFlyoutVisible ? { ...activeBadge } : {}]}
            {...injectTestId('attendee-messaging-badge-container')}
          >
            <MessageBadge />
          </div>
        )}
      </div>
      {!iconOnly && (
        <div css={contentWrapper}>
          <span css={content} {...injectTestId('attendee-messaging-message-icon-text')}>
            {translate('AttendeeMessaging-MessageIcon-Text')}
          </span>
          <MessageBadge />
        </div>
      )}
    </div>
  );
}

export default React.memo(MessageIcon);
